import React from "react";
import IntegrationConversionSettings from "./IntegrationConversionSettings";
import {Box, Button, DialogActions, DialogContent, List, ListItem, StepConnector, TextField, useMediaQuery, useTheme, ListItemIcon, ListItemText, Step, StepLabel, Stepper, Link} from "@mui/material";
import {IntegrationDialog as Dialog, IntegrationHeader, IntegrationBackButton} from "./IntegrationDesign";
import {integrationActions} from "../../../store/actions/integrationAction";
import {connect} from "react-redux";
import {useCallback, useEffect, useState} from "react";
import Spinner from "../../spinner/Spinner";
import {Check} from "@mui/icons-material";
import {useFieldArray, useForm} from "react-hook-form";
import {alertActions} from "../../../store/actions/alertActions";
import useCustomStyles from "../../utils/UseCustomStyles";

const customStyles = (theme) => ({
    listItem: {
        paddingTop: 0,
        paddingLeft: 0,
        paddingBottom: 0
    },
    listItemIcon: {
        minWidth: 42
    },
    stepper: {
        margin: "0 auto",
        width: "100%",
        maxWidth: 800,
        padding: "0px 24px 24px 24px"
    },
    stepConnectorLine: {
        borderColor: theme.palette.background.tableBorder
    },
});

function MgidIntegration(props) {
    const theme = useTheme();
    const classes = useCustomStyles(customStyles, theme);
    const {integration, resetIntegration, removeIntegrationEvent, create, update, find, alert} = props;

    const steps = ['Integrate Account', 'Conversion API'];

    const integrationId = parseInt(props?.match?.params?.id);

    const [open, setOpen] = useState(true);
    const [activeStep, setActiveStep] = useState(1);
    const [removeEventIndex, setRemoveEventIndex] = useState(null);
    const [step1Data, setStep1Data] = useState(null);

    const {register, handleSubmit, control, setValue, watch, getValues, formState: {errors}} = useForm();
    const {fields, remove, append} = useFieldArray({
        control,
        name: "events"
    });

    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

    const handleClose = useCallback((event, reason) => {
        if (reason === 'backdropClick') {
            return false;
        }

        resetIntegration();
        setOpen(false);
        props.history.push({pathname: "/settings/integrations", state: {from: props.location}});
    }, [resetIntegration, props.history, props.location]);

    const handleOnSubmitStep1 = (data) => {
        setStep1Data(data);
        setActiveStep(2);
    };

    const handleOnSubmit = (data) => {
        if (integrationId > 0) {
            update({
                integrationId: integrationId,
                name: step1Data?.name,
                apiClient: step1Data?.apiClient,
                apiSecret: step1Data?.apiSecret,
                enableConversionAPI: data?.enableConversionAPI,
                events: data?.events
            });
        } else {
            create({
                name: step1Data?.name,
                apiClient: step1Data?.apiClient,
                apiSecret: step1Data?.apiSecret,
                enableConversionAPI: data?.enableConversionAPI,
                events: data?.events
            });
        }
    };

    const appendNewEvent = () => {
        append({
            eventId: 0,
            name: "",
            includePayout: false
        });
    };

    const handleDeleteIntegrationEvent = useCallback(({eventId, index}) => {
        if (eventId === 0) {
            remove(index);
            return false;
        }
        setRemoveEventIndex({eventId, index});
    }, [remove, setRemoveEventIndex]);

    useEffect(() => {
        if (removeEventIndex !== null) {
            remove(removeEventIndex?.index);
            removeIntegrationEvent({integrationId, eventId: removeEventIndex?.eventId});
        }
    }, [integrationId, remove, removeEventIndex, removeIntegrationEvent]);

    useEffect(() => {
        if (integrationId > 0) {
            find(integrationId);
        }
    }, [find, integrationId]);

    useEffect(() => {
        if (activeStep === 1 && integrationId > 0 && integration?.currentIntegration !== null) {
            if (integration?.currentIntegration?.id === integrationId) {
                setValue("name", integration?.currentIntegration?.networkName);
                for (const cred of integration?.currentIntegration?.integrationCredentials) {
                    if (cred?.keyName === "mgid_api_client") {
                        setValue("apiClient", cred?.keyValue);
                    }
                    if (cred?.keyName === "mgid_api_secret") {
                        setValue("apiSecret", cred?.keyValue);
                    }
                }
            }
        }
    }, [activeStep, setValue, integration?.currentIntegration, integrationId]);

    // Set step 1 form data.
    useEffect(() => {
        if (activeStep === 1 && step1Data !== null) {
            setValue("name", step1Data?.name);
            setValue("apiClient", step1Data?.apiClient);
            setValue("apiSecret", step1Data?.apiSecret);
        }
    }, [activeStep, setValue, step1Data]);

    useEffect(() => {
        if (activeStep === 2 && integration?.currentIntegration !== null) {
            for (const cred of integration?.currentIntegration?.integrationCredentials) {
                if (cred?.keyName === "mgid_enable_conversion_api") {
                    if (cred?.keyValue === "true") {
                        setValue("enableConversionAPI", true);
                    } else {
                        setValue("enableConversionAPI", false);
                    }
                }
            }

            if (getValues("events")?.length === 0) {
                let events = [];
                integration?.currentIntegration?.integrationEvents?.forEach((event) => {
                    events.push({
                        eventId: event?.id,
                        name: event?.name,
                        includePayout: event?.includePayout
                    });
                });
                setValue("events", events);
            }
        }
    }, [activeStep, setValue, integration?.currentIntegration, getValues]);

    useEffect(() => {
        if (alert?.successMsg !== "") {
            handleClose();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [alert?.successMsg, handleClose]);

    return (
        <Dialog open={open}
                onClose={handleClose}
                fullWidth={true}
                fullScreen={fullScreen}
                maxWidth={"sm"}
                aria-labelledby="mgid-account-integration"
        >
            <IntegrationHeader name="Mgid" logo="/assets/images/traffic-sources/mgid.svg" id="mgid-account-integration"/>
            <DialogContent>
                <Stepper
                    alternativeLabel={fullScreen}
                    activeStep={activeStep}
                    connector={<StepConnector classes={{line: classes?.stepConnectorLine}}/>}
                    classes={{root: classes?.stepper}}
                >
                    {steps.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                {activeStep === 1 &&
                    <>
                        <List>
                            <ListItem className={classes?.listItem}>
                                <ListItemIcon className={classes?.listItemIcon}>
                                    <Check color={"primary"}/>
                                </ListItemIcon>
                                <ListItemText disableTypography primary="Auto-matching for all campaigns."/>
                            </ListItem>
                            <ListItem className={classes?.listItem}>
                                <ListItemIcon className={classes?.listItemIcon}>
                                    <Check color={"primary"}/>
                                </ListItemIcon>
                                <ListItemText disableTypography primary="Check the status of your campaigns."/>
                            </ListItem>
                            <ListItem className={classes?.listItem}>
                                <ListItemIcon className={classes?.listItemIcon}>
                                    <Check color={"primary"}/>
                                </ListItemIcon>
                                <ListItemText disableTypography primary="Change the bid value for widget and teaser."/>
                            </ListItem>
                            <ListItem className={classes?.listItem}>
                                <ListItemIcon className={classes?.listItemIcon}>
                                    <Check color={"primary"}/>
                                </ListItemIcon>
                                <ListItemText disableTypography
                                              primary="Pause or resume campaigns, widget and teaser."/>
                            </ListItem>
                            <ListItem className={classes?.listItem}>
                                <ListItemIcon className={classes?.listItemIcon}>
                                    <Check color={"primary"}/>
                                </ListItemIcon>
                                <ListItemText disableTypography
                                              primary="Sync cost on campaign level and widget level every 30 min."/>
                            </ListItem>
                        </List>
                        <p>
                            Please check our integration
                            guide <Link target="_blank" underline="hover" rel="noreferrer"
                                        href="https://skro.eu/docs/integrations/mgid-integration">here</Link>.
                        </p>

                        {integration?.loading?.integration && <Spinner overComponentBox={true}/>}
                        <form onSubmit={handleSubmit(handleOnSubmitStep1)} autoComplete={"off"}>
                            <Box display="flex" flexWrap={"wrap"}>
                                <Box py={0.75} width="100%">
                                    <TextField
                                        error={!!errors.name}
                                        variant="outlined"
                                        margin="dense"
                                        id="name"
                                        label="Integration Name*"
                                        {...register("name", {required: true, maxLength: 255, minLength: 2})}
                                        name="name"
                                        size="small"
                                        type="text"
                                        placeholder="Enter your integration name"
                                        fullWidth={true}
                                        defaultValue="MGID"
                                        slotProps={{
                                            inputLabel: {
                                                shrink: true
                                            }
                                        }}
                                    />
                                </Box>
                                <Box py={0.75} width="100%">
                                    <TextField
                                        error={!!errors.apiClient}
                                        variant="outlined"
                                        margin="dense"
                                        id="apiClient"
                                        label="API Client*"
                                        {...register("apiClient", {required: true, maxLength: 255, minLength: 2})}
                                        name="apiClient"
                                        size="small"
                                        type="text"
                                        placeholder="Enter your API Client"
                                        fullWidth={true}
                                        slotProps={{
                                            inputLabel: {
                                                shrink: true
                                            }
                                        }}
                                    />
                                </Box>
                                <Box py={0.75} width="100%">
                                    <TextField
                                        error={!!errors.apiSecret}
                                        variant="outlined"
                                        margin="dense"
                                        id="apiSecret"
                                        label="API Secret*"
                                        {...register("apiSecret", {required: true, maxLength: 255, minLength: 2})}
                                        name="apiSecret"
                                        size="small"
                                        type="text"
                                        placeholder="Enter your API Secret"
                                        fullWidth={true}
                                        slotProps={{
                                            inputLabel: {
                                                shrink: true
                                            }
                                        }}
                                    />
                                </Box>
                            </Box>
                        </form>
                    </>
                }

                {activeStep === 2 &&
                    <Box sx={{position: "relative"}}>
                        {(integration?.loading?.conversionApi || integration?.loading?.integration) &&
                            <Spinner overComponentBox={true}/>}
                        <IntegrationConversionSettings provider="MGID" control={control} register={register} errors={errors}
                            enabled={watch("enableConversionAPI")} eventFields={fields}
                            onAddEvent={appendNewEvent} onDeleteEvent={handleDeleteIntegrationEvent}/>
                    </Box>
                }
            </DialogContent>
            <DialogActions>
                <Box display="flex" justifyContent="flex-end">
                    <Box mr={3}>
                        <Button onClick={handleClose} color="inherit" variant="text">
                            Close
                        </Button>
                    </Box>
                    {activeStep === 1 &&
                        <Box>
                            <Button onClick={handleSubmit(handleOnSubmitStep1)} color="primary" variant="contained">
                                Next
                            </Button>
                        </Box>
                    }
                    {activeStep === 2 &&
                        <>
                            <Box mr={1}>
                                <IntegrationBackButton onClick={() => setActiveStep(1)}/>
                            </Box>
                            <Box>
                                <Button onClick={handleSubmit(handleOnSubmit)} color="primary" variant="contained">
                                    Save & Close
                                </Button>
                            </Box>
                        </>
                    }
                </Box>
            </DialogActions>
        </Dialog>
    );
}

const mapStateToProps = (state) => {
    return {
        integration: state.integration,
        alert: state.alert
    };
};

const mapDispatchToProps = {
    resetIntegration: integrationActions.reset,
    find: integrationActions.findIntegration,
    create: integrationActions.createMgidIntegration,
    update: integrationActions.updateMgidIntegration,
    error: alertActions.error,
    removeIntegrationEvent: integrationActions.removeIntegrationEventFromCurrentIntegration
};

export default connect(mapStateToProps, mapDispatchToProps)(MgidIntegration);
